// Comprehensive Google Fonts list organized by categories
// All fonts are free and available via Google Fonts API

export interface FontOption {
  name: string;
  category: string;
  weights: number[];
  popularity: 'high' | 'medium' | 'low';
  usage: string;
}

export const GOOGLE_FONTS: FontOption[] = [
  // Sans Serif - Most Popular for UI/UX
  { name: 'Inter', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'high', usage: 'Modern UI/UX standard' },
  { name: 'Roboto', category: 'sans-serif', weights: [100, 300, 400, 500, 700, 900], popularity: 'high', usage: 'Google Material Design' },
  { name: 'Open Sans', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800], popularity: 'high', usage: 'Universal readability' },
  { name: 'Lato', category: 'sans-serif', weights: [100, 300, 400, 700, 900], popularity: 'high', usage: 'Friendly professional' },
  { name: 'Montserrat', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'high', usage: 'Healthcare & Finance' },
  { name: 'Source Sans Pro', category: 'sans-serif', weights: [200, 300, 400, 600, 700, 900], popularity: 'high', usage: 'Adobe ecosystem' },
  { name: 'Poppins', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'high', usage: 'Modern geometric' },
  { name: 'Nunito Sans', category: 'sans-serif', weights: [200, 300, 400, 600, 700, 800, 900], popularity: 'medium', usage: 'Rounded friendly' },
  { name: 'Work Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Creative agencies' },
  { name: 'DM Sans', category: 'sans-serif', weights: [400, 500, 700], popularity: 'medium', usage: 'Design-focused' },
  
  // Corporate & Business
  { name: 'IBM Plex Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700], popularity: 'medium', usage: 'Corporate IBM style' },
  { name: 'Fira Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Mozilla design' },
  { name: 'Oxygen', category: 'sans-serif', weights: [300, 400, 700], popularity: 'medium', usage: 'Clean corporate' },
  { name: 'Ubuntu', category: 'sans-serif', weights: [300, 400, 500, 700], popularity: 'medium', usage: 'Canonical Ubuntu' },
  { name: 'Noto Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Google universal' },
  
  // Tech & Startups
  { name: 'Space Grotesk', category: 'sans-serif', weights: [300, 400, 500, 600, 700], popularity: 'medium', usage: 'Tech startups' },
  { name: 'Manrope', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800], popularity: 'medium', usage: 'Modern tech' },
  { name: 'Public Sans', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Government tech' },
  { name: 'Plus Jakarta Sans', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800], popularity: 'medium', usage: 'Indonesian modern' },
  { name: 'Outfit', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Fashion tech' },
  
  // Serif - Traditional & Professional
  { name: 'Merriweather', category: 'serif', weights: [300, 400, 700, 900], popularity: 'high', usage: 'Trust & authority' },
  { name: 'Playfair Display', category: 'serif', weights: [400, 500, 600, 700, 800, 900], popularity: 'high', usage: 'Luxury & premium' },
  { name: 'Source Serif Pro', category: 'serif', weights: [200, 300, 400, 600, 700, 900], popularity: 'medium', usage: 'Adobe serif' },
  { name: 'Crimson Text', category: 'serif', weights: [400, 600, 700], popularity: 'medium', usage: 'Legal documents' },
  { name: 'Lora', category: 'serif', weights: [400, 500, 600, 700], popularity: 'medium', usage: 'Reading comfort' },
  { name: 'Libre Baskerville', category: 'serif', weights: [400, 700], popularity: 'medium', usage: 'Classic elegance' },
  { name: 'Cormorant Garamond', category: 'serif', weights: [300, 400, 500, 600, 700], popularity: 'low', usage: 'Editorial style' },
  
  // Display & Headings
  { name: 'Oswald', category: 'display', weights: [200, 300, 400, 500, 600, 700], popularity: 'high', usage: 'Bold headlines' },
  { name: 'Bebas Neue', category: 'display', weights: [400], popularity: 'medium', usage: 'Impact headlines' },
  { name: 'Lexend', category: 'display', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Reading proficiency' },
  { name: 'Comfortaa', category: 'display', weights: [300, 400, 500, 600, 700], popularity: 'medium', usage: 'Friendly rounded' },
  
  // Monospace - Technical
  { name: 'JetBrains Mono', category: 'monospace', weights: [100, 200, 300, 400, 500, 600, 700, 800], popularity: 'high', usage: 'Developer tools' },
  { name: 'Fira Code', category: 'monospace', weights: [300, 400, 500, 600, 700], popularity: 'medium', usage: 'Programming ligatures' },
  { name: 'Source Code Pro', category: 'monospace', weights: [200, 300, 400, 500, 600, 700, 900], popularity: 'medium', usage: 'Adobe monospace' },
  { name: 'Space Mono', category: 'monospace', weights: [400, 700], popularity: 'medium', usage: 'Retro tech' },
  
  // Additional Popular Fonts
  { name: 'Raleway', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'high', usage: 'Elegant minimal' },
  { name: 'Nunito', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800, 900], popularity: 'high', usage: 'Friendly rounded' },
  { name: 'Rubik', category: 'sans-serif', weights: [300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Slightly rounded' },
  { name: 'Karla', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800], popularity: 'medium', usage: 'Grotesque style' },
  { name: 'Quicksand', category: 'sans-serif', weights: [300, 400, 500, 600, 700], popularity: 'medium', usage: 'Geometric sans' },
  { name: 'Barlow', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Transport inspired' },
  { name: 'Heebo', category: 'sans-serif', weights: [100, 200, 300, 400, 500, 600, 700, 800, 900], popularity: 'medium', usage: 'Hebrew origin' },
  { name: 'Mukti', category: 'sans-serif', weights: [200, 300, 400, 500, 600, 700, 800], popularity: 'low', usage: 'Devanagari support' },
];

export const FONT_CATEGORIES = {
  'sans-serif': 'Sans Serif',
  'serif': 'Serif', 
  'display': 'Display',
  'monospace': 'Monospace'
};

export const POPULAR_FONTS = GOOGLE_FONTS.filter(font => font.popularity === 'high');

export const getFontsByCategory = (category: string) => {
  return GOOGLE_FONTS.filter(font => font.category === category);
};

export const getFontWeights = (fontName: string): number[] => {
  const font = GOOGLE_FONTS.find(f => f.name === fontName);
  return font?.weights || [400, 700];
};

export const buildGoogleFontsUrl = (fontName: string, weights?: number[]): string => {
  const font = GOOGLE_FONTS.find(f => f.name === fontName);
  const weightsToLoad = weights || font?.weights || [400, 700];
  const weightsString = weightsToLoad.join(';');
  
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${weightsString}&display=swap`;
};

export const buildBunnyFontsUrl = (fontName: string, weights?: number[]): string => {
  const font = GOOGLE_FONTS.find(f => f.name === fontName);
  const weightsToLoad = weights || font?.weights || [400, 700];
  const weightsString = weightsToLoad.join(';');
  
  return `https://fonts.bunny.net/css?family=${encodeURIComponent(fontName)}:wght@${weightsString}&display=swap`;
};